import { useState } from 'react'
import { Box, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import TaPassFail from './TaPassFail'
import TaElements from './TaElements'
import EvalCategorySection from './EvalCategorySection'
import EvalGroupSection from './EvalGroupSection'
import TAChip, { ChipKind } from './TAChip'
import { FS_SM, FS_XS, ICON_SM } from '../common/theme'
import { hasAnswerText } from '../common/common'
import { useAppActions } from '../AppContext'
import { addToast } from '../services/Toasts'
import TaScoringTemplateModal from '../tabs/modals/TaScoringTemplateModal'
import { useAppProject } from '../AppContext'

interface TaEvalScoredProps {
  bideval: any
  question?: any
  onEval?: () => void
  onSaveTemplate?: (group: any) => void
  onEditTemplate?: (group: any) => void
  // Read-only display mode (e.g. when mounted inside the wizard's
  // score-details modal). Hides the scoring-elements editor and the
  // per-group save / edit template icons so the user can't mutate
  // anything from inside the popout.
  readOnly?: boolean
  // Optional previous-version bideval. When set the eval view
  // renders inline deltas (pass/fail flips per checkpoint, count +
  // score deltas per category / group / overall). `prevBideval`
  // renders on the LEFT (older) of the arrow, the primary `bideval`
  // on the RIGHT (current). Assumes both evals share the same
  // template — rows pair up by elementid/name and fall back to index.
  prevBideval?: any
}

// Pass / fail / pending status for a single checkpoint result.
type CpStatus = 'pass' | 'fail' | 'pending'

// Filter affordance — `all` shows every checkpoint, `pass` / `fail`
// show only matching ones, hiding categories / groups that end up
// empty after filtering. Pending checkpoints only appear under
// `all` (they haven't been processed so they don't satisfy a
// passed-or-failed predicate).
type EvalFilter = 'all' | 'pass' | 'fail'

function statusOf(q: any): CpStatus {
  if (!q?.processed) return 'pending'
  return q.passed ? 'pass' : 'fail'
}

// Does this checkpoint pass the current filter? The status compared
// against is the headline / end-state — in compare mode that's the
// right-side bideval (the answer the user is iterating towards), in
// non-compare it's the primary.
function passesFilter(filter: EvalFilter, headline: CpStatus): boolean {
  if (filter === 'all') return true
  return headline === filter
}

function CheckpointRow({ q, qPrev }: { q: any; qPrev?: any }) {
  // Left = prev (older), right = current. The end-state the user is
  // iterating towards is `q` — that's the "headline" status the
  // compact-mode chip + the filter both key off.
  const leftStatus: CpStatus | null = qPrev ? statusOf(qPrev) : null
  const rightStatus: CpStatus = statusOf(q)
  const inCompare = qPrev != null
  const changed = inCompare && leftStatus !== rightStatus
  // Unchanged-in-compare rows start collapsed: just the end-state
  // chip + dimmer text. The user can still expand them to see the
  // before chip, the arrow, and the reason drawer. Changed rows
  // start expanded so the flip jumps out.
  const unchanged = inCompare && !changed
  const reasonLeft = qPrev?.reason
  const reasonRight = q?.reason
  const reasonAvailable = !!reasonLeft || !!reasonRight
  const [expanded, setExpanded] = useState(!unchanged && (!q.passed || changed))

  // Headline chip in compact (collapsed-unchanged) mode shows the
  // end-state; everything else gets the full before → after diff or
  // (non-compare) the single status.
  const showCompactHeader = unchanged && !expanded
  const headlineStatus: CpStatus = rightStatus
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pl: 1, pr: 0.5, py: showCompactHeader ? 0.25 : 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {showCompactHeader ? (
          <TAChip kind={headlineStatus} />
        ) : inCompare ? (
          <>
            <TAChip kind={leftStatus as ChipKind} />
            <ArrowRightAltIcon sx={{ fontSize: ICON_SM, opacity: 0.5, flexShrink: 0 }} />
            <TAChip kind={rightStatus} />
          </>
        ) : (
          <TAChip kind={rightStatus} />
        )}
        <Typography
          variant="caption"
          sx={{
            flex: 1, fontSize: FS_SM, lineHeight: 1.4,
            opacity: showCompactHeader ? 0.65 : 1,
          }}
        >
          {q.checkpoint ?? qPrev?.checkpoint}
        </Typography>
        {(unchanged || reasonAvailable) && (
          <IconButton size="small" onClick={() => setExpanded(v => !v)} sx={{ p: 0.2, flexShrink: 0 }}>
            {expanded
              ? <ExpandLessIcon sx={{ fontSize: ICON_SM }} />
              : <ExpandMoreIcon sx={{ fontSize: ICON_SM }} />}
          </IconButton>
        )}
      </Box>
      <Collapse in={expanded && reasonAvailable}>
        <Box sx={{ mt: 0.25, pl: 0.5 }}>
          {inCompare ? (
            <>
              {!!reasonLeft && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600, opacity: 0.5 }}>
                    Reason (before)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: FS_SM, lineHeight: 1.4, opacity: 0.8 }}>
                    {reasonLeft}
                  </Typography>
                </Box>
              )}
              {!!reasonRight && (
                <Box>
                  <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600, opacity: 0.5 }}>
                    Reason (after)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: FS_SM, lineHeight: 1.4, opacity: 0.8 }}>
                    {reasonRight}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600, opacity: 0.5 }}>
                Reason
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: FS_SM, lineHeight: 1.4, opacity: 0.8 }}>
                {reasonLeft}
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

function CategorySection({ c, cPrev, filter = 'all' }: { c: any; cPrev?: any; filter?: EvalFilter }) {
  const checkpoints = c?.checkpoints ?? cPrev?.checkpoints ?? []
  if (checkpoints.length === 0) return null
  // Filter checkpoints by their headline status — the headline is
  // always the *current* (primary) bideval; the prev side is just for
  // comparison context. Drop the entire category card when nothing
  // survives the filter.
  const visibleCheckpoints = (c?.checkpoints ?? []).filter((q: any) => {
    return passesFilter(filter, statusOf(q))
  })
  if (visibleCheckpoints.length === 0) return null
  // Left rail colour reflects the current pass/fail balance — the
  // post-tweak end state the user is iterating towards.
  const refCat = c
  const passed = refCat?.numpassed ?? 0
  const failed = refCat?.numfailed ?? 0
  const railColor =
    passed > 0 && failed === 0 ? 'success.main' :
      failed > 0 && passed === 0 ? 'error.main' :
        passed > 0 && failed > 0 ? 'warning.main' :
          'divider'
  // Pair each current checkpoint with its prev counterpart by
  // checkpoint text (templates are shared so they line up cleanly;
  // fall back to index for safety).
  const prevByText: Map<string, any> = new Map()
  if (cPrev) {
    for (let i = 0; i < (cPrev.checkpoints?.length ?? 0); i++) {
      const cp = cPrev.checkpoints[i]
      if (cp?.checkpoint) prevByText.set(cp.checkpoint, cp)
    }
  }
  return (
    <EvalCategorySection
      name={c?.category ?? cPrev?.category}
      railColor={railColor}
      headerExtras={
        <>
          {cPrev && (
            <>
              <TaPassFail passCount={cPrev?.numpassed ?? 0} failCount={cPrev?.numfailed ?? 0} />
              <ArrowRightAltIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
            </>
          )}
          <TaPassFail passCount={c?.numpassed ?? 0} failCount={c?.numfailed ?? 0} />
        </>
      }
    >
      {visibleCheckpoints.map((q: any, qi: number) => {
        const qPrev = cPrev ? (prevByText.get(q.checkpoint) ?? cPrev.checkpoints?.[qi]) : undefined
        return <CheckpointRow key={q.id ?? qi} q={q} qPrev={qPrev} />
      })}
    </EvalCategorySection>
  )
}

function GroupSection({ g, gPrev, onSaveTemplate, onEditTemplate, readOnly, filter = 'all' }: {
  g: any
  gPrev?: any
  onSaveTemplate?: (g: any) => void
  onEditTemplate?: (g: any) => void
  readOnly?: boolean
  filter?: EvalFilter
}) {
  // Check whether any category in this group has at least one
  // checkpoint matching the active filter — skip the whole group
  // card when nothing inside is visible. The headline is always the
  // current (primary) `q`; prev is just for context.
  const groupHasVisible = (g?.checkpointcategories ?? []).some((c: any) => {
    return (c?.checkpoints ?? []).some((q: any) => {
      return passesFilter(filter, statusOf(q))
    })
  })
  if (!groupHasVisible) return null
  // Pair categories by name; index fallback.
  const prevByCat: Map<string, any> = new Map()
  if (gPrev) {
    for (let i = 0; i < (gPrev.checkpointcategories?.length ?? 0); i++) {
      const cat = gPrev.checkpointcategories[i]
      if (cat?.category) prevByCat.set(cat.category, cat)
    }
  }
  // Left = prev score (older), right = current score.
  const prevScore = gPrev?.scoreint ?? 0
  const currScore = g?.scoreint ?? 0
  const onTemplateClick = () => {
    if (g.elementid === 'auto') onSaveTemplate?.(g)
    else onEditTemplate?.(g)
  }
  return (
    <EvalGroupSection
      name={g?.name ?? gPrev?.name}
      actions={
        !readOnly && (
          <Tooltip title="Edit Scoring Template">
            <IconButton size="small" onClick={onTemplateClick} sx={{ p: 0.25 }}>
              <EditOutlinedIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        )
      }
      headerExtras={
        <>
          {gPrev && (
            <>
              <TaPassFail passCount={gPrev?.numpassed ?? 0} failCount={gPrev?.numfailed ?? 0} />
              <ArrowRightAltIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
            </>
          )}
          <TaPassFail passCount={g?.numpassed ?? 0} failCount={g?.numfailed ?? 0} />
          <Tooltip title={gPrev ? `${prevScore}% → ${currScore}%` : `${currScore}%`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 32, justifyContent: 'flex-end' }}>
              {gPrev && (
                <>
                  <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600 }}>
                    {prevScore}%
                  </Typography>
                  <ArrowRightAltIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
                </>
              )}
              <Typography
                variant="caption"
                sx={{
                  fontSize: FS_SM, fontWeight: gPrev ? 700 : 600,
                  color: !gPrev ? 'text.primary'
                    : currScore > prevScore ? 'success.main'
                      : currScore < prevScore ? 'error.main'
                        : 'text.primary',
                }}
              >
                {currScore}%
              </Typography>
            </Box>
          </Tooltip>
        </>
      }
    >
      {(g?.checkpointcategories ?? []).map((c: any, ci: number) => {
        const cPrev = gPrev
          ? (prevByCat.get(c.category) ?? gPrev.checkpointcategories?.[ci])
          : undefined
        return <CategorySection key={c.id ?? ci} c={c} cPrev={cPrev} filter={filter} />
      })}
    </EvalGroupSection>
  )
}

export default function TaEvalScored({ bideval, question, onSaveTemplate, onEditTemplate, readOnly = false, prevBideval }: TaEvalScoredProps) {
  const scoreType = bideval?.scoretypestring
  const isScored = scoreType === 'scored'
  const isInfoOnly = scoreType === 'infoonly'
  const isPassFail = scoreType === 'passfail'
  const hasAnswer = hasAnswerText(question)
  const passfailCheckpoint = bideval?.checkpointeval?.groups?.[0]?.checkpointcategories?.[0]?.checkpoints?.[0]

  // Unified template flow. Both the "Save as Scoring Template" and
  // "Edit Scoring Template" actions open the same modal — clicking
  // the save icon on an auto group seeds the modal with the group's
  // current categories + checkpoints (no id yet, so it'll create);
  // clicking the edit icon resolves the saved template from
  // currentProject.scoringtemplates and the modal updates that.
  // After a successful save the modal calls `onSaved(saved)`, which
  // stamps the persisted id + name back onto the source group so the
  // chip header flips to "Edit" on the next render.
  const { currentProject } = useAppProject()
  const { updateQuestion } = useAppActions()
  // The group the modal was launched from, plus the template payload
  // it should edit (existing template or a freshly-seeded one).
  const [templateModal, setTemplateModal] = useState<{ sourceGroup: any; template: any } | null>(null)

  // Pass / fail / all checkpoint filter. Affects the scored view
  // (groups + categories + checkpoints) — pending checkpoints only
  // appear under `all`.
  const [filter, setFilter] = useState<EvalFilter>('all')
  // Count checkpoints by headline status so the filter chips can
  // surface totals. Uses prevBideval when present (end state) so
  // the numbers match what the filter actually hides.
  const filterCounts = (() => {
    const source = prevBideval ?? bideval
    let all = 0, pass = 0, fail = 0
    for (const g of source?.checkpointeval?.groups ?? []) {
      for (const c of g.checkpointcategories ?? []) {
        for (const cp of c.checkpoints ?? []) {
          all++
          const s = statusOf(cp)
          if (s === 'pass') pass++
          else if (s === 'fail') fail++
        }
      }
    }
    return { all, pass, fail }
  })()

  const buildSeedFromGroup = (g: any): any => ({
    name: g?.name ?? '',
    type: 'score',
    categories: (g?.checkpointcategories ?? []).map((c: any) => ({
      category: c?.category ?? '',
      checkpoints: (c?.checkpoints ?? [])
        .map((cp: any) => typeof cp === 'string' ? cp : (cp?.checkpoint ?? ''))
        .filter(Boolean),
    })),
  })

  const handleSaveTemplateClick = (g: any) => {
    if (onSaveTemplate) { onSaveTemplate(g); return }
    setTemplateModal({ sourceGroup: g, template: buildSeedFromGroup(g) })
  }

  const handleEditTemplateClick = (g: any) => {
    if (onEditTemplate) { onEditTemplate(g); return }
    const tpl = (currentProject?.scoringtemplates ?? [])
      .find((t: any) => (t.id ?? t.elementid) === g.elementid)
    if (tpl) setTemplateModal({ sourceGroup: g, template: tpl })
    else addToast('error', 'Template not found in this project.')
  }

  const handleTemplateSaved = (saved: any) => {
    if (!templateModal) return
    const sourceG = templateModal.sourceGroup
    const newId = saved?.id ?? saved?.elementid
    const newName = saved?.name
    if (!newId) return
    // Two propagation cases share one walk:
    //   • Create — the launching question's source group flips from
    //     `elementid='auto'` to the freshly-persisted id + name.
    //   • Edit — every question (including the launching one) that
    //     selected the previous-id template inherits the new name so
    //     the chip header reads correctly. The link itself stays
    //     stable via `elementid`.
    const previousId = templateModal.template?.id ?? templateModal.template?.elementid
    const sourceQsha = question?.questionsha
    const sourceGId = sourceG?.id ?? sourceG?.elementid

    for (const q of currentProject?.questions ?? []) {
      const qGroups = q?.bideval?.checkpointeval?.groups ?? []
      let touched = false
      const newGroups = qGroups.map((og: any) => {
        const ogId = og?.id ?? og?.elementid
        const isSourceGroup =
          q.questionsha === sourceQsha && (ogId === sourceGId || og === sourceG)
        const isOtherSelectionOfSameTemplate =
          !!previousId && ogId === previousId
        if (isSourceGroup || isOtherSelectionOfSameTemplate) {
          touched = true
          return { ...og, elementid: newId, name: newName ?? og.name }
        }
        return og
      })
      if (touched && q.bideval) {
        // Narrow on `q.bideval` so the spread satisfies BidEvalWebResult's
        // required `scoringelements` field. A question without a bideval
        // can't have a matching group anyway, so the `touched && q.bideval`
        // guard is the same predicate the loop body already implies.
        updateQuestion(q.questionsha, {
          bideval: {
            ...q.bideval,
            checkpointeval: { ...(q.bideval.checkpointeval ?? {}), groups: newGroups },
          },
        })
      }
    }
  }

  // Pair groups left-to-right by elementid first (stable across saves)
  // then by group name; fall back to index for the rare case where
  // neither lines up. Templates are shared between the two evals so
  // these usually all resolve cleanly.
  const prevByElementId: Map<string, any> = new Map()
  const prevByName: Map<string, any> = new Map()
  if (prevBideval) {
    for (const rg of prevBideval?.checkpointeval?.groups ?? []) {
      if (rg?.elementid) prevByElementId.set(rg.elementid, rg)
      if (rg?.name) prevByName.set(rg.name, rg)
    }
  }
  const findGroupPrev = (lg: any, idx: number): any | undefined => {
    if (!prevBideval) return undefined
    if (lg?.elementid && prevByElementId.has(lg.elementid)) return prevByElementId.get(lg.elementid)
    if (lg?.name && prevByName.has(lg.name)) return prevByName.get(lg.name)
    return prevBideval?.checkpointeval?.groups?.[idx]
  }

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ px: 0.75, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <TaElements
            mode="scoring"
            question={question}
            elements={bideval?.scoringelements ?? []}
          />
        </Box>
      )}



      {isInfoOnly && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.75 }}>
          <Typography variant="caption" sx={{ flex: 1, fontSize: FS_SM }}>
            {hasAnswer ? 'You have provided an answer' : 'You have NOT provided an answer'}
          </Typography>
          {hasAnswer ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'success.main' }}>
              <CheckIcon sx={{ fontSize: ICON_SM }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Passed</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'error.main' }}>
              <CloseIcon sx={{ fontSize: ICON_SM }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Failed</Typography>
            </Box>
          )}
        </Box>
      )}

      {isPassFail && passfailCheckpoint && (
        <Box sx={{ px: 0.75, py: 0.5 }}>
          <CheckpointRow
            q={passfailCheckpoint}
            qPrev={prevBideval?.checkpointeval?.groups?.[0]?.checkpointcategories?.[0]?.checkpoints?.[0]}
          />
        </Box>
      )}

      {isScored && (
        <Box sx={{ p: 0.75 }}>
          {/* Filter chips — show all checkpoints, only passed, or
              only failed. Counts come off the end-state bideval in
              compare mode so the numbers match what the filter
              actually hides. Pending checkpoints only show under
              "All". */}
          {filterCounts.all > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, px: 0.25 }}>
              <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.55, mr: 0.25 }}>Show</Typography>
              {/* Three chips, active one filled (primary), others
                  outlined. Styling mirrors the selection chip in
                  BidQuestionsLeftPanel — height 20, fontSize 0.7rem. */}
              <Chip
                size="small"
                label={`All (${filterCounts.all})`}
                color={filter === 'all' ? 'primary' : 'default'}
                variant={filter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setFilter('all')}
                sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer' }}
              />
              <Chip
                size="small"
                icon={<CheckIcon sx={{ fontSize: '12px !important' }} />}
                label={`Passed (${filterCounts.pass})`}
                color={filter === 'pass' ? 'primary' : 'default'}
                variant={filter === 'pass' ? 'filled' : 'outlined'}
                onClick={() => setFilter('pass')}
                sx={{
                  fontSize: '0.7rem', height: 20, cursor: 'pointer',
                  '& .MuiChip-icon': { ml: '4px', mr: '-4px' },
                }}
              />
              <Chip
                size="small"
                icon={<CloseIcon sx={{ fontSize: '12px !important' }} />}
                label={`Failed (${filterCounts.fail})`}
                color={filter === 'fail' ? 'primary' : 'default'}
                variant={filter === 'fail' ? 'filled' : 'outlined'}
                onClick={() => setFilter('fail')}
                sx={{
                  fontSize: '0.7rem', height: 20, cursor: 'pointer',
                  '& .MuiChip-icon': { ml: '4px', mr: '-4px' },
                }}
              />
            </Box>
          )}
          {(bideval?.checkpointeval?.groups ?? []).map((g: any, gi: number) => (
            <GroupSection
              key={g.id ?? gi}
              g={g}
              gPrev={findGroupPrev(g, gi)}
              onSaveTemplate={handleSaveTemplateClick}
              onEditTemplate={handleEditTemplateClick}
              readOnly={readOnly}
              filter={filter}
            />
          ))}
        </Box>
      )}

      <TaScoringTemplateModal
        open={!!templateModal}
        template={templateModal?.template ?? null}
        defaultProjectSha={currentProject?.projectsha ?? ''}
        onSaved={handleTemplateSaved}
        onClose={() => setTemplateModal(null)}
      />
    </Box>
  )
}
